import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import type { AdminTemplate } from '../lib/api'
import { ProjectMenuItem } from './ProjectMenuItem'
import { IconAlertTriangle, IconBlocks, IconCloudUpload, IconDots, IconLayout, IconLayoutBoard, IconLoader2, IconPower, IconRobotOff, IconRotate2, IconSettings, IconTrash } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { taxonomyLabelKey } from '@webbuilder/schemas'
import { activeLanguage, useI18n } from '../lib/i18n'

type TemplateAction = 'publish' | 'archive' | 'activate' | 'reset' | 'delete' | 'aiOn' | 'aiOff'

type Props = {
  template: AdminTemplate
  menuOpen: boolean
  actionPending: boolean
  showThumbnail: boolean
  onMenuOpenChange: (open: boolean) => void
  onOpenCanvas: () => void
  onEdit: () => void
  onAction: (action: TemplateAction) => void
}

const MENU_WIDTH = 208
const VIEWPORT_GAP = 12

const KIND_LABEL_KEY: Record<string, string> = {
  website: 'templateModal.fullPage',
  page: 'templateModal.kindPage',
  section: 'templateModal.kindSection',
  header: 'templateModal.kindHeader',
  footer: 'templateModal.kindFooter',
  component: 'templateModal.kindComponent',
}

export function TemplateAdminCard({
  template,
  menuOpen,
  actionPending,
  showThumbnail,
  onMenuOpenChange,
  onOpenCanvas,
  onEdit,
  onAction,
}: Props) {
  const { t } = useI18n()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({ opacity: 0 })
  /* Lo que la IA usa para elegirlo —cómo está armado y su fondo—, con su
     nombre. Las etiquetas sueltas de antes sólo si no hay facetas. */
  const facetTags = [
    ...(template.facets.layout ?? []).map((value) => t(taxonomyLabelKey('layout', value))),
    ...(template.facets.colorScheme ?? []).map((value) => t(taxonomyLabelKey('colorScheme', value))),
  ]
  const tags = (facetTags.length ? facetTags : [...template.styleTags, ...template.aiTags]).slice(0, 3)
  const status = template.status === 'published' || template.status === 'archived' ? template.status : 'draft'
  const kindLabel = KIND_LABEL_KEY[template.kind] ? t(KIND_LABEL_KEY[template.kind]!) : template.kind
  // Un sitio entero se clasifica por industria, un bloque por lo que es.
  const categoryKey = taxonomyLabelKey(template.kind === 'website' ? 'bestFor' : 'category', template.category)
  const categoryLabel = categoryKey === template.category ? template.category : t(categoryKey)
  const readiness = template.kind === 'website' ? null : template.aiReadiness
  /* En qué idiomas se inserta: el suyo y los que tiene traducidos (ver
     `templateTexts.ts`). Un diseño, sus idiomas; ya no una copia por idioma. */
  const languages = template.facets.languages?.length ? template.facets.languages : template.facets.language ? [template.facets.language] : []
  const updatedAt = template.updatedAt
    ? new Date(template.updatedAt).toLocaleDateString(activeLanguage(), { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  useLayoutEffect(() => {
    if (!menuOpen) return
    const placeMenu = () => {
      const button = menuButtonRef.current
      if (!button) return
      const rect = button.getBoundingClientRect()
      const menuHeight = menuRef.current?.offsetHeight ?? 240
      const left = Math.min(
        window.innerWidth - MENU_WIDTH - VIEWPORT_GAP,
        Math.max(VIEWPORT_GAP, rect.right - MENU_WIDTH),
      )
      const roomBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP
      const top = roomBelow >= menuHeight + 8
        ? rect.bottom + 8
        : Math.max(VIEWPORT_GAP, rect.top - menuHeight - 8)
      setMenuStyle({
        position: 'fixed',
        top,
        left,
        right: 'auto',
        bottom: 'auto',
        width: MENU_WIDTH,
        maxHeight: `calc(100vh - ${VIEWPORT_GAP * 2}px)`,
        overflowY: 'auto',
        opacity: 1,
      })
    }
    placeMenu()
    const frame = window.requestAnimationFrame(placeMenu)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMenuOpenChange(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen, onMenuOpenChange])

  return (
    <article className={`dash-template-card${menuOpen ? ' menu-open' : ''}`}>
      <button className="dash-template-card-media" type="button" onClick={onOpenCanvas}>
        {showThumbnail && template.thumbnailUrl ? (
          <img src={template.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <span className={`dash-template-card-placeholder is-${template.kind}`} aria-hidden="true">
            {template.kind === 'website' ? <IconLayoutBoard size={34} /> : <IconBlocks size={34} />}
            <small>{template.category}</small>
            {/* Publicada pero sin imagen: no hay captura de ella, así que en el
                catálogo del editor no hay nada que enseñar. Se arregla abriendo
                el canvas y volviendo a publicar, que captura al vuelo. */}
            {showThumbnail && template.status === 'published' ? <em>{t('templateCard.noThumbnail')}</em> : null}
          </span>
        )}
        <span className="dash-template-card-scrim" aria-hidden="true" />
        <span className="dash-template-card-open">
          <IconLayout size={16} /> {t('templateCard.openCanvas')}
        </span>
        <span className="dash-template-card-badges">
          <span className={`dash-template-card-status is-${status}`}>{t(`templateCard.status.${status}`)}</span>
          {template.hasUnpublishedChanges ? <span className="dash-template-card-status is-changes">{t('templateCard.unpublished')}</span> : null}
        </span>
        {template.isPremium ? <span className="dash-template-card-premium">{t('templateCard.premium')}</span> : null}
      </button>

      <div className="dash-template-card-body">
        <div className="dash-template-card-heading">
          <div>
            <strong title={template.name}>{template.name}</strong>
            <span>{kindLabel} · {categoryLabel}{languages.length ? ` · ${languages.map((language) => language.toUpperCase()).join(' · ')}` : ''}</span>
          </div>
          <button
            ref={menuButtonRef}
            className={`bento-circle dash-template-card-menu-btn${menuOpen ? ' active' : ''}`}
            type="button"
            aria-label={t('common.optionsOf', { name: template.name })}
            aria-expanded={menuOpen}
            disabled={actionPending}
            onClick={() => onMenuOpenChange(!menuOpen)}
          >
            {actionPending ? <IconLoader2 className="dash-spin" size={16} /> : <IconDots size={17} />}
          </button>
        </div>
        {template.description
          ? <p>{template.description}</p>
          : <p className="is-empty">{t('templateCard.noDescription')}</p>}
        {tags.length ? (
          <div className="dash-template-card-tags">
            {tags.map((tag) => <em key={tag}>{tag}</em>)}
          </div>
        ) : null}
        <div className="dash-template-card-meta">
          {/* Apagado en la IA (`manualOnly`) manda sobre si está listo: un
              bloque completo pero quitado de la IA decía «AI ready». */}
          {readiness && template.facets.manualOnly ? (
            <span className="dash-template-card-ai is-off">
              <IconRobotOff size={13} />
              {t('templateCard.aiDisabled')}
            </span>
          ) : readiness ? (
            <span className={`dash-template-card-ai${readiness.ready ? ' is-ready' : ''}`}>
              {readiness.ready ? <VelticaMascot outline size={15} /> : <IconAlertTriangle size={13} />}
              {readiness.ready
                ? t('templateCard.aiReady')
                : t('templateCard.aiMissing', { missing: readiness.blocking.filter((issue) => issue !== 'manualOnly').map((issue) => t(`templateAi.short.${issue}`)).join(', ') })}
            </span>
          ) : <span />}
          {updatedAt ? <small>{updatedAt}</small> : null}
        </div>
      </div>

      {menuOpen ? createPortal(
        <>
          <div className="dash-template-menu-backdrop" role="presentation" onClick={() => onMenuOpenChange(false)} />
          <div ref={menuRef} className="project-menu dash-template-card-menu" style={menuStyle} role="menu">
            <ProjectMenuItem icon={<IconLayout size={14} />} label={t('templateCard.openCanvas')} onClick={() => { onMenuOpenChange(false); onOpenCanvas() }} />
            <ProjectMenuItem icon={<IconSettings size={14} />} label={t('templateCard.editData')} onClick={onEdit} />
            {template.hasUnpublishedChanges ? (
              <>
                <div className="project-menu-rule" />
                <ProjectMenuItem icon={<IconCloudUpload size={14} />} label={t('templateCard.publish')} onClick={() => onAction('publish')} />
                <ProjectMenuItem icon={<IconRotate2 size={14} />} label={t('templateCard.discard')} onClick={() => onAction('reset')} />
              </>
            ) : null}
            <div className="project-menu-rule" />
            {/* Si la IA lo usa al crear sitios, sin abrir la ficha. */}
            {template.kind !== 'website' ? (
              template.facets.manualOnly
                ? <ProjectMenuItem icon={<VelticaMascot outline size={14} />} label={t('templateCard.aiOn')} onClick={() => onAction('aiOn')} />
                : <ProjectMenuItem icon={<VelticaMascot outline size={14} />} label={t('templateCard.aiOff')} onClick={() => onAction('aiOff')} />
            ) : null}
            {template.status === 'archived' ? (
              <ProjectMenuItem icon={<IconPower size={14} />} label={t('templateCard.activate')} onClick={() => onAction('activate')} />
            ) : (
              <ProjectMenuItem icon={<IconPower size={14} />} label={t('templateCard.archive')} onClick={() => onAction('archive')} />
            )}
            <div className="project-menu-rule" />
            <ProjectMenuItem icon={<IconTrash size={14} />} label={t('templateCard.delete')} danger onClick={() => onAction('delete')} />
          </div>
        </>,
        document.body,
      ) : null}
    </article>
  )
}
